/** Admin侧边栏导航 — 纯动态菜单（消费 /info 返回的 menus 树渲染）
 *  首页独立渲染 + M 分组默认折叠 + 路由匹配自动展开 */
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Collapse, Badge } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { useTodoCount } from '@/hooks/useTodoCount';
import type { MenuNode } from '@bluplanet/shared';
import { resolveIcon } from '@/utils/iconMap';
import { COLORS } from '@/theme/colors';

const SIDEBAR_WIDTH = 240;

/** 统一补上前导斜杠，确保与 react-router location.pathname 格式一致 */
function normalizePath(path?: string): string {
  if (!path) return '';
  return path.startsWith('/') ? path : '/' + path;
}

/** 递归查找某个分组及其子菜单中是否有匹配当前 path 的项 */
function groupMatchesPath(group: MenuNode, path: string): boolean {
  const children = group.children ?? [];
  for (const child of children) {
    const childPath = normalizePath(child.path);
    if (child.menuType === 'C' && childPath && path.startsWith(childPath)) {
      return true;
    }
    // 递归检查子 M（嵌套分组）
    if (child.menuType === 'M' && groupMatchesPath(child, path)) {
      return true;
    }
  }
  return false;
}

export default function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { menus } = useAppContext();
  const { count: todoCount } = useTodoCount();

  // 展开的分组 ID 列表（默认空=全部折叠）
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // 路由变化时，自动展开当前路由所在的分组
  useEffect(() => {
    if (!menus || menus.length === 0) return;
    const currentPath = location.pathname;

    // 查找匹配的分组
    const matchedGroupIds: string[] = [];
    const groups = menus.filter((m) => m.menuType === 'M');
    for (const group of groups) {
      if (groupMatchesPath(group, currentPath)) {
        matchedGroupIds.push(group.id);
      }
    }

    setExpandedGroups((prev) => {
      // 合并已手动展开的 + 当前路由匹配的分组
      const merged = new Set([...prev, ...matchedGroupIds]);
      return Array.from(merged);
    });
  }, [location.pathname, menus]);

  // 切换分组折叠/展开
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      }
      return [...prev, groupId];
    });
  }, []);

  const isGroupExpanded = (groupId: string): boolean => expandedGroups.includes(groupId);

  // 判断菜单项是否激活
  const isActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  // 渲染单个菜单项 C（导航链接）
  const renderMenuItem = (item: MenuNode) => {
    if (item.menuType !== 'C' || item.visible === 0 || item.status === 0) return null;
    const itemPath = normalizePath(item.path);
    if (!itemPath) return null;
    const active = isActive(itemPath);
    // S2-2：待办中心菜单项挂红点徽标（Badge），计数来自 TodoCountProvider（60s 轮询 + focus 刷新）
    const isTodoMenu = itemPath === '/admin/fulfillment/todo';
    const labelNode = isTodoMenu ? (
      <Badge badgeContent={todoCount} max={99} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
        {item.menuName}
      </Badge>
    ) : (
      item.menuName
    );
    return (
      <Box
        key={item.id}
        onClick={() => { navigate(itemPath); onNavigate?.(); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, mx: 1, my: 0.5, px: 2, py: 1,
          borderRadius: '8px', fontSize: '13px',
          color: active ? COLORS.primary.dark : COLORS.grey[700],
          backgroundColor: active ? COLORS.primary.light : 'transparent',
          fontWeight: active ? 600 : 400,
          cursor: 'pointer',
          '&:hover': { backgroundColor: active ? COLORS.primary.light : COLORS.grey[100] },
          transition: 'all 0.15s ease',
        }}
      >
        <Box component="span" sx={{
          fontSize: '18px', width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, color: active ? COLORS.primary.main : COLORS.grey[500],
        }}>
          {resolveIcon(item.icon)}
        </Box>
        <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {labelNode}
        </Box>
      </Box>
    );
  };

  // 渲染分组标题行（含展开/折叠箭头）
  const renderGroupHeader = (group: MenuNode) => {
    const expanded = isGroupExpanded(group.id);
    return (
      <Box
        onClick={() => toggleGroup(group.id)}
        sx={{
          display: 'flex', alignItems: 'center', cursor: 'pointer',
          px: 2, py: 1.25, mt: 0.5,
          userSelect: 'none',
          '&:hover': { backgroundColor: COLORS.grey[50] },
        }}
      >
        <Box component="span" sx={{
          mr: 0.75, color: COLORS.grey[400], display: 'flex', alignItems: 'center',
          transition: 'transform 0.2s ease',
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        }}>
          <ExpandMore fontSize="small" />
        </Box>
        <Typography sx={{
          fontSize: '11px', fontWeight: 600, color: COLORS.grey[600],
          letterSpacing: '0.3px',
        }}>
          {group.menuName}
        </Typography>
      </Box>
    );
  };

  // 渲染一个 M 分组（折叠面板）
  const renderGroup = (group: MenuNode) => {
    const children = (group.children ?? []).filter(
      (c) => c.menuType === 'C' && c.visible !== 0 && c.status !== 0,
    );
    const expanded = isGroupExpanded(group.id);

    return (
      <Box key={group.id} sx={{ mb: 0.25 }}>
        {renderGroupHeader(group)}
        <Collapse in={expanded} timeout={200}>
          {children.map((c) => renderMenuItem(c))}
        </Collapse>
      </Box>
    );
  };

  // 渲染首页独立导航项（始终可见，不折叠）
  const renderHomeItem = (homeItem: MenuNode) => {
    const homePath = normalizePath(homeItem.path);
    if (!homePath) return null;
    const active = isActive(homePath);
    return (
      <Box
        key={homeItem.id}
        sx={{ mb: 0.5 }}
      >
        <Box
          onClick={() => { navigate(homePath); onNavigate?.(); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mx: 1, my: 0.5, px: 2, py: 1,
            borderRadius: '8px', fontSize: '13px',
            color: active ? COLORS.primary.dark : COLORS.grey[800],
            backgroundColor: active ? COLORS.primary.light : 'transparent',
            fontWeight: active ? 600 : 500,
            cursor: 'pointer',
            '&:hover': { backgroundColor: active ? COLORS.primary.light : COLORS.grey[100] },
            transition: 'all 0.15s ease',
          }}
        >
          <Box component="span" sx={{
            fontSize: '18px', width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, color: active ? COLORS.primary.main : COLORS.grey[500],
          }}>
            {resolveIcon(homeItem.icon)}
          </Box>
          <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {homeItem.menuName}
          </Box>
        </Box>
      </Box>
    );
  };

  /**
   * 主渲染逻辑：
   * 1. 找到"首页"C节点（menuType=C, parent_id=虚拟根, menuName=首页）
   * 2. 剩余 M 节点作为分组渲染
   */
  const renderNavigation = () => {
    if (!menus || menus.length === 0) return null;

    // 分离首页和分组
    const homeItem = menus.find(
      (m) => m.menuType === 'C' && m.menuName === '首页',
    );
    const groups = menus.filter((m) => m.menuType === 'M');

    return (
      <>
        {homeItem && renderHomeItem(homeItem)}
        {groups.map((group) => renderGroup(group))}
      </>
    );
  };

  return (
    <Box sx={{
      width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, height: '100vh',
      backgroundColor: '#FFFFFF', borderRight: `1px solid ${COLORS.primary.line}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Logo 区域 — 点击跳转首页，高度与 TopBar 对齐 56px */}
      <Box
        onClick={() => { navigate('/admin/dashboard'); onNavigate?.(); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, px: 2, height: 56, minHeight: 56,
          borderBottom: `1px solid ${COLORS.primary.line}`, cursor: 'pointer',
          '&:hover': { backgroundColor: COLORS.grey[50] },
        }}
      >
        <Box component="img" src="/logo/star-icon.jpg" alt="BluPlanet" sx={{
          width: 32, height: 32, borderRadius: '8px', objectFit: 'contain', flexShrink: 0,
        }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.text.primary, lineHeight: 1.3 }}>
            蓝星履约平台
          </Typography>
          <Typography sx={{ fontSize: '10px', color: COLORS.grey[500], lineHeight: 1.3, mt: 0.25 }}>
            BluPlanet OS
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', py: 0.75 }}>
        {renderNavigation()}
      </Box>
    </Box>
  );
}
